/**
 * Shared error/status message component — Round 6 polish audit
 * (docs/AUDIT-2026-08-polish.md). Resolves AUD-004, 005, 006, 008, 009,
 * 010, 011, and 020 as one change: this repo had no shared error/status
 * display before this (every page reinvented its own `{error && <p>}`),
 * and NOTHING anywhere used `aria-live`/`role="alert"`/`role="status"`
 * (AUD-020 — confirmed zero matches sitewide) — a screen-reader user whose
 * focus had moved on got no announcement that a request had failed or
 * succeeded.
 *
 *   - variant="error"  -> role="alert"  (assertive — interrupts to announce
 *     a failure the builder needs to notice).
 *   - variant="status" -> role="status" (polite — announces a confirmation,
 *     e.g. "Saved.", without interrupting).
 *
 * `className` lets a call site keep its own existing box styling
 * (background/padding/etc. — already nearly pixel-identical across pages)
 * so migrating a call site to this component causes no visual diff. When
 * omitted, the component's own CSS module supplies a sensible default for
 * any future, not-yet-styled call site.
 */
import type { ReactNode } from "react";
import styles from "./StatusMessage.module.css";

export type StatusVariant = "error" | "status";

interface StatusMessageProps {
  variant: StatusVariant;
  message: string;
  className?: string;
  /** Extra inline content after the message — e.g. a Retry/Dismiss button. */
  children?: ReactNode;
}

export function StatusMessage({ variant, message, className, children }: StatusMessageProps) {
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={className ?? (variant === "error" ? styles.error : styles.status)}
    >
      {message}
      {children}
    </div>
  );
}

// Friendly error-message mapping (AUD-004/005/006/008/009/010) lives in its
// own module — see ./friendlyErrorMessage — so this file only exports a
// component (react-refresh/only-export-components requires that). Import
// `friendlyErrorMessage` directly from there, not from here.
